import React, { Dispatch, SetStateAction, useState } from 'react'
import { format } from 'date-fns'
import { SurgeryDetailsContent } from './content'
import {
  BackButtonContainer,
  BreadCrumbsContainer,
  Container,
  Message,
  RelatedMessage,
  RelatedProcedureSkeleton,
  ReturnArrowButton,
  RelatedContentContainer,
  CustomSkeleton
} from './styles'
import { ModalListProcedures } from '../ModalComponents/ModalListProcedures'
import { ModalItemListProcedures } from '../ModalComponents/ModalItemListProcedures'
import { AnatomopathologicalTestDetailsContent } from '../AnatomopathologicalTestDetails/content'
import { HealthProcedureDetails } from '../healthProcedureDetails'
import { MedicalNoteDetailsContent } from '../MedicalNoteDetails/content'
import { ModalButtonComeBack } from '../ModalComponents/ModalButtonComeBack'
import { ModalHeaderSkeleton } from '../ModalComponents/ModalHeader/Skeleton'
import { useFetchAnatomopathologicalTestsOfSurgery, useFetchMedicalNotesOfSurgery, useFetchSurgery } from '~/hooks'
import { BreadCrumbs, BreadCrumbsProps, DataNotFound } from '~/components/shared'
import { IconComponent } from '~/assets/icons/component'
import { IAnatomopathologicalTest, IHealthProcedures, IMedicalNote } from '~/interfaces'
import { getTimestamp, hasObjectValue } from '~/utils'

interface Props {
  healthProcedure: IHealthProcedures
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
}

export const SurgeryDetails = ({ healthProcedure, showModal, setShowModal }: Props): JSX.Element => {
  const [breadCrumbsLevels, setBreadCrumbsLevels] = useState<BreadCrumbsProps['breadCrumbsLevels']>([])

  const {
    data: surgery,
    isLoading: isSurgeryLoading,
    error: surgeryError
  } = useFetchSurgery({
    pathParams: { surgeryId: Number(healthProcedure.id) }
  })
  const hasSurgery = hasObjectValue({ ...surgery })

  const [selectedMedicalNote, setSelectedMedicalNote] = useState<IMedicalNote | null>(null)
  const {
    data: medicalNotes,
    isLoading: isMedicalNotesLoading,
    error: medicalNotesError
  } = useFetchMedicalNotesOfSurgery(
    { pathParams: { surgeryId: healthProcedure.id }, queryParams: { title: 'Descrição cirúrgica' } },
    { enabled: hasSurgery }
  )

  const [selectedAnatomopathological, setSelectedAnatomopathological] = useState<IAnatomopathologicalTest | null>(null)
  const {
    data: anatomopathologicalTests,
    isLoading: isAnatomopathologicalTestsLoading,
    error: anatomopathologicalTestsError
  } = useFetchAnatomopathologicalTestsOfSurgery(
    { pathParams: { surgeryId: healthProcedure.id } },
    { enabled: hasSurgery }
  )

  const returnToSurgeryDetailsModal = (): void => {
    setSelectedMedicalNote(null)
    setSelectedAnatomopathological(null)
  }

  const handleOpenMedicalNote = (noteId: number) => {
    if (medicalNotes?.length) {
      const selectedNote = medicalNotes.find(note => note.noteId === noteId) ?? null
      setSelectedMedicalNote(selectedNote)
      setBreadCrumbsLevels([
        { text: String(surgery?.description), handleClick: returnToSurgeryDetailsModal },
        { text: 'Nota Médica', handleClick: () => {} }
      ])
    }
  }

  const handleOpenAnatomoPathologicalTest = (testId: string) => {
    if (anatomopathologicalTests?.length) {
      const selectedEvent = anatomopathologicalTests.find(event => event.id === testId) ?? null
      setSelectedAnatomopathological(selectedEvent)
      setBreadCrumbsLevels([
        { text: String(surgery?.description), handleClick: returnToSurgeryDetailsModal },
        { text: 'Exames Anatomopatológico', handleClick: () => {} }
      ])
    }
  }

  const renderContent = () => {
    if (surgeryError)
      return (
        <Message>
          <DataNotFound text={surgeryError.message} />
        </Message>
      )

    if (selectedMedicalNote ?? selectedAnatomopathological) {
      return (
        <>
          <ReturnArrowButton onClick={returnToSurgeryDetailsModal}>
            <IconComponent name="arrowLeft" />
          </ReturnArrowButton>
          <BreadCrumbsContainer>
            <BreadCrumbs breadCrumbsLevels={breadCrumbsLevels} />
          </BreadCrumbsContainer>
          <RelatedContentContainer>
            {renderMedicalNote()}
            {renderAnatomopathologicalTest()}
          </RelatedContentContainer>
          <BackButtonContainer>
            <ModalButtonComeBack onClick={returnToSurgeryDetailsModal} />
          </BackButtonContainer>
        </>
      )
    }

    if (surgery) {
      return (
        <SurgeryDetailsContent
          surgeryData={surgery}
          renderRelatedAnatomopathologicalTests={() => {
            return (
              <ModalListProcedures title={'Relatórios Anatomopatológicos'}>
                {anatomopathologicalTestsError && (
                  <RelatedMessage>{anatomopathologicalTestsError.message}</RelatedMessage>
                )}
                {isAnatomopathologicalTestsLoading && <RelatedProcedureSkeleton />}

                {anatomopathologicalTests
                  ? anatomopathologicalTests.map(({ id, description, startDate }) => (
                      <ModalItemListProcedures
                        key={id}
                        type="exameAnatomopatologico"
                        label={description}
                        value={getTimestamp(startDate) > 0 ? format(new Date(startDate), 'dd/MM - HH:mm') : undefined}
                        onClick={() => handleOpenAnatomoPathologicalTest(id)}
                      />
                    ))
                  : null}
              </ModalListProcedures>
            )
          }}
          renderRelatedMedicalNotes={() => {
            return (
              <ModalListProcedures title={'Notas Médicas'}>
                {medicalNotesError && <RelatedMessage>{medicalNotesError.message}</RelatedMessage>}
                {isMedicalNotesLoading && <RelatedProcedureSkeleton />}

                {medicalNotes
                  ? medicalNotes.map(({ noteId, title, creationDate }) => (
                      <ModalItemListProcedures
                        key={noteId}
                        type="notaMedica"
                        label={title}
                        value={
                          getTimestamp(creationDate) > 0 ? format(new Date(creationDate), 'dd/MM - HH:mm') : undefined
                        }
                        onClick={() => handleOpenMedicalNote(noteId)}
                      />
                    ))
                  : null}
              </ModalListProcedures>
            )
          }}
        />
      )
    }
  }

  const renderMedicalNote = () => {
    if (!selectedMedicalNote) return
    return <MedicalNoteDetailsContent medicalNoteData={selectedMedicalNote} />
  }
  const renderAnatomopathologicalTest = () => {
    if (!selectedAnatomopathological) return
    return <AnatomopathologicalTestDetailsContent anatomopathologicalTestData={selectedAnatomopathological} />
  }
  const renderLoading = () => {
    return (
      <>
        <ModalHeaderSkeleton />
        <CustomSkeleton />
        <CustomSkeleton />
      </>
    )
  }

  return (
    <HealthProcedureDetails showModal={showModal} setShowModal={setShowModal} viewType="SideModal">
      <Container>{isSurgeryLoading ? renderLoading() : renderContent()}</Container>
    </HealthProcedureDetails>
  )
}
